import React from "react";
import styled from "styled-components";
import { Card, Empty, Skeleton, Space, Tag, Typography } from "antd";

const { Text } = Typography;

const TrendCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;

  .ant-card-head-title {
    color: #005249;
    font-weight: 700;
  }
`;

const Legend = styled(Space)`
  margin-bottom: 16px;
`;

const ChartViewport = styled.div`
  overflow-x: auto;
`;

const ChartSvg = styled.svg`
  display: block;
  min-width: 680px;
  width: 100%;
  height: auto;
`;

const AxisLine = styled.line`
  stroke: #d9d9d9;
  stroke-width: 1;
`;

const GridLine = styled.line`
  stroke: #f0f0f0;
  stroke-width: 1;
`;

const SeriesLine = styled.polyline`
  fill: none;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

const SeriesPoint = styled.circle`
  stroke: #ffffff;
  stroke-width: 2;
`;

const AxisLabel = styled.text`
  fill: #8c8c8c;
  font-size: 11px;
`;

const PointTitle = ({ children }) => React.createElement("title", null, children);

const CHART_WIDTH = 720;
const CHART_HEIGHT = 260;
const PADDING = {
  top: 24,
  right: 28,
  bottom: 44,
  left: 64,
};

const RevenueTrendChart = ({ trend, loading = false }) => {
  if (loading) {
    return (
      <TrendCard title="Revenue Trend" data-testid="revenue-trend-loading">
        <Skeleton active paragraph={{ rows: 6 }} />
      </TrendCard>
    );
  }

  if (!hasTrendData(trend)) {
    return (
      <TrendCard title="Revenue Trend">
        <Empty
          description="No revenue trend data for the selected filters"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </TrendCard>
    );
  }

  const chart = buildChartModel(trend);

  return (
    <TrendCard
      title="Revenue Trend"
      extra={<Text type="secondary">{formatGranularity(trend.granularity)}</Text>}
    >
      <Legend size={[8, 8]} wrap>
        <Tag color="cyan">Booked revenue</Tag>
        <Tag color="green">Collected revenue</Tag>
        <Tag color="default">Previous collected</Tag>
      </Legend>
      <ChartViewport>
        <ChartSvg
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          role="img"
          aria-label="Revenue trend chart"
          data-testid="revenue-trend-chart"
        >
          {chart.gridLines.map((gridLine) => (
            <GridLine
              key={gridLine.y}
              x1={PADDING.left}
              x2={CHART_WIDTH - PADDING.right}
              y1={gridLine.y}
              y2={gridLine.y}
            />
          ))}
          <AxisLine
            x1={PADDING.left}
            x2={CHART_WIDTH - PADDING.right}
            y1={CHART_HEIGHT - PADDING.bottom}
            y2={CHART_HEIGHT - PADDING.bottom}
          />
          <AxisLine
            x1={PADDING.left}
            x2={PADDING.left}
            y1={PADDING.top}
            y2={CHART_HEIGHT - PADDING.bottom}
          />
          {chart.series.map((series) => (
            <SeriesLine
              key={series.key}
              points={series.points}
              stroke={series.color}
              strokeDasharray={series.dashed ? "6 6" : undefined}
            />
          ))}
          {chart.series.flatMap((series) =>
            series.pointModels.map((point) => (
              <SeriesPoint
                key={`${series.key}-${point.bucket.key}`}
                cx={point.x}
                cy={point.y}
                r={4}
                fill={series.color}
              >
                <PointTitle>
                  {series.label} • {point.bucket.label}: {formatCurrency(point.value)}
                </PointTitle>
              </SeriesPoint>
            ))
          )}
          {chart.xLabels.map((label) => (
            <AxisLabel key={label.key} x={label.x} y={CHART_HEIGHT - 16} textAnchor="middle">
              {label.label}
            </AxisLabel>
          ))}
          {chart.yLabels.map((label) => (
            <AxisLabel key={label.value} x={PADDING.left - 10} y={label.y + 4} textAnchor="end">
              {formatCompactCurrency(label.value)}
            </AxisLabel>
          ))}
        </ChartSvg>
      </ChartViewport>
    </TrendCard>
  );
};

function hasTrendData(trend) {
  return (trend?.current || []).some(
    (bucket) => bucket.revenueBooked > 0 || bucket.revenueCollected > 0
  );
}

function buildChartModel(trend) {
  const currentBuckets = trend.current || [];
  const previousBuckets = trend.previous || [];
  const maxValue = Math.max(
    1,
    ...currentBuckets.flatMap((bucket) => [
      bucket.revenueBooked || 0,
      bucket.revenueCollected || 0,
    ]),
    ...previousBuckets.map((bucket) => bucket.revenueCollected || 0)
  );
  const xStep =
    currentBuckets.length > 1
      ? (CHART_WIDTH - PADDING.left - PADDING.right) / (currentBuckets.length - 1)
      : 0;

  const createPoint = (bucket, index, value) => ({
    bucket,
    value,
    x: PADDING.left + xStep * index,
    y: scaleY(value, maxValue),
  });

  const series = [
    {
      key: "booked",
      label: "Booked revenue",
      color: "#13c2c2",
      pointModels: currentBuckets.map((bucket, index) =>
        createPoint(bucket, index, bucket.revenueBooked || 0)
      ),
    },
    {
      key: "collected",
      label: "Collected revenue",
      color: "#52c41a",
      pointModels: currentBuckets.map((bucket, index) =>
        createPoint(bucket, index, bucket.revenueCollected || 0)
      ),
    },
    {
      key: "previous-collected",
      label: "Previous collected",
      color: "#8c8c8c",
      dashed: true,
      pointModels: previousBuckets.slice(0, currentBuckets.length).map((bucket, index) =>
        createPoint(bucket, index, bucket.revenueCollected || 0)
      ),
    },
  ].map((seriesItem) => ({
    ...seriesItem,
    points: seriesItem.pointModels.map((point) => `${point.x},${point.y}`).join(" "),
  }));

  return {
    series,
    gridLines: [0, 0.25, 0.5, 0.75, 1].map((ratio) => ({
      y: PADDING.top + ratio * (CHART_HEIGHT - PADDING.top - PADDING.bottom),
    })),
    xLabels: createXAxisLabels(currentBuckets, xStep),
    yLabels: [0, 0.5, 1].map((ratio) => ({
      value: Math.round(maxValue * ratio),
      y: scaleY(maxValue * ratio, maxValue),
    })),
  };
}

function createXAxisLabels(buckets, xStep) {
  if (buckets.length <= 7) {
    return buckets.map((bucket, index) => ({
      key: bucket.key,
      label: bucket.label,
      x: PADDING.left + xStep * index,
    }));
  }

  const indexes = new Set([0, Math.floor((buckets.length - 1) / 2), buckets.length - 1]);
  return buckets
    .map((bucket, index) => ({
      key: bucket.key,
      label: bucket.label,
      x: PADDING.left + xStep * index,
      visible: indexes.has(index),
    }))
    .filter((label) => label.visible);
}

function scaleY(value, maxValue) {
  const drawableHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  return PADDING.top + (1 - Number(value || 0) / maxValue) * drawableHeight;
}

function formatGranularity(granularity) {
  if (granularity === "month") return "Monthly buckets";
  if (granularity === "week") return "Weekly buckets";
  return "Daily buckets";
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatCompactCurrency(value) {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(value || 0));
}

export default RevenueTrendChart;
